import React, { createContext, useContext, useEffect, useState } from 'react'
import { doc, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from './AuthContext'
import { CartState, CartItem, Product } from '../types'
import toast from 'react-hot-toast'

interface CartContextType {
  cart: CartState | null
  loading: boolean
  addToCart: (product: Product, quantity?: number) => Promise<void>
  updateQuantity: (productId: string, quantity: number) => Promise<void>
  removeFromCart: (productId: string) => Promise<void>
  clearCart: () => Promise<void>
  getCartItemCount: () => number
  getCartTotal: () => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

interface CartProviderProps {
  children: React.ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { currentUser } = useAuth()
  const [cart, setCart] = useState<CartState | null>(null)
  const [loading, setLoading] = useState(false)

  // Calculate cart totals
  const calculateTotals = (items: CartItem[]) => {
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0)
    const subtotal = items.reduce((sum, item) => sum + (item.productSnapshot.price * item.quantity), 0)
    const estimatedTax = subtotal * 0.17 // 17% tax in Pakistan
    const estimatedTotal = subtotal + estimatedTax

    return {
      itemCount,
      subtotal,
      estimatedTax,
      estimatedTotal
    }
  }

  // Create or update cart in Firestore
  const updateCartInFirestore = async (items: CartItem[]) => {
    if (!currentUser) return

    const totals = calculateTotals(items)
    const cartData: CartState = {
      items,
      totals,
      lastUpdated: new Date() as any
    }

    try {
      await setDoc(doc(db, 'carts', currentUser.uid), cartData)
    } catch (error) {
      console.error('Error updating cart:', error)
      toast.error('Failed to update cart')
    }
  }

  // Add item to cart
  const addToCart = async (product: Product, quantity = 1) => {
    try {
      setLoading(true)

      const currentItems = cart?.items || []
      const existingItemIndex = currentItems.findIndex(item => item.productId === product.id)

      let newItems: CartItem[]

      if (existingItemIndex >= 0) {
        // Update existing item
        newItems = [...currentItems]
        newItems[existingItemIndex].quantity += quantity
      } else {
        // Add new item
        const newItem: CartItem = {
          productId: product.id,
          quantity,
          addedAt: new Date() as any,
          productSnapshot: {
            name: product.name,
            price: product.price,
            image: product.media.images[0] || '',
            availability: product.inventory.available > 0
          }
        }
        newItems = [...currentItems, newItem]
      }

      if (currentUser) {
        await updateCartInFirestore(newItems)
      } else {
        // For guest users, store in localStorage
        const totals = calculateTotals(newItems)
        const guestCart: CartState = {
          items: newItems,
          totals,
          lastUpdated: new Date() as any
        }
        localStorage.setItem('guestCart', JSON.stringify(guestCart))
        setCart(guestCart)
      }

      toast.success(`${product.name} added to cart`)
    } catch (error) {
      console.error('Error adding to cart:', error)
      toast.error('Failed to add item to cart')
    } finally {
      setLoading(false)
    }
  }

  // Update item quantity
  const updateQuantity = async (productId: string, quantity: number) => {
    if (quantity <= 0) {
      await removeFromCart(productId)
      return
    }

    try {
      setLoading(true)

      const currentItems = cart?.items || []
      const newItems = currentItems.map(item =>
        item.productId === productId ? { ...item, quantity } : item
      )

      if (currentUser) {
        await updateCartInFirestore(newItems)
      } else {
        const totals = calculateTotals(newItems)
        const guestCart: CartState = {
          items: newItems,
          totals,
          lastUpdated: new Date() as any
        }
        localStorage.setItem('guestCart', JSON.stringify(guestCart))
        setCart(guestCart)
      }

      toast.success('Cart updated')
    } catch (error) {
      console.error('Error updating quantity:', error)
      toast.error('Failed to update quantity')
    } finally {
      setLoading(false)
    }
  }

  // Remove item from cart
  const removeFromCart = async (productId: string) => {
    try {
      setLoading(true)

      const currentItems = cart?.items || []
      const newItems = currentItems.filter(item => item.productId !== productId)

      if (currentUser) {
        await updateCartInFirestore(newItems)
      } else {
        const totals = calculateTotals(newItems)
        const guestCart: CartState = {
          items: newItems,
          totals,
          lastUpdated: new Date() as any
        }
        localStorage.setItem('guestCart', JSON.stringify(guestCart))
        setCart(guestCart)
      }

      toast.success('Item removed from cart')
    } catch (error) {
      console.error('Error removing from cart:', error)
      toast.error('Failed to remove item')
    } finally {
      setLoading(false)
    }
  }

  // Clear entire cart
  const clearCart = async () => {
    try {
      setLoading(true)

      if (currentUser) {
        await deleteDoc(doc(db, 'carts', currentUser.uid))
      } else {
        localStorage.removeItem('guestCart')
        setCart(null)
      }

      toast.success('Cart cleared')
    } catch (error) {
      console.error('Error clearing cart:', error)
      toast.error('Failed to clear cart')
    } finally {
      setLoading(false)
    }
  }

  // Get cart item count
  const getCartItemCount = () => {
    return cart?.totals.itemCount || 0
  }

  // Get cart total
  const getCartTotal = () => {
    return cart?.totals.estimatedTotal || 0
  }

  // Load cart data
  useEffect(() => {
    if (currentUser) {
      // Load cart from Firestore for authenticated users
      const unsubscribe = onSnapshot(
        doc(db, 'carts', currentUser.uid),
        (doc) => {
          if (doc.exists()) {
            setCart(doc.data() as CartState)
          } else {
            setCart(null)
          }
        },
        (error) => {
          console.error('Error loading cart:', error)
        }
      )

      return unsubscribe
    } else {
      // Load cart from localStorage for guest users
      const guestCartData = localStorage.getItem('guestCart')
      if (guestCartData) {
        try {
          const guestCart = JSON.parse(guestCartData) as CartState
          setCart(guestCart)
        } catch (error) {
          console.error('Error parsing guest cart:', error)
          localStorage.removeItem('guestCart')
        }
      } else {
        setCart(null)
      }
    }
  }, [currentUser])

  const value: CartContextType = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartItemCount,
    getCartTotal
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}